import { BigNumber, utils } from 'ethers';
import React from 'react';
import dayjs from 'dayjs';
import {
  useAppDispatch,
  useAppSelector,
  useFromChainWalletAddress,
  useToChainWalletAddress,
} from '.';
import {
  selectBridgeSlice,
  setTxCurrentHistory,
  setTxHasError,
} from '../redux/slices/bridgeSlice';
import BridgeBackend from '../services/bridge-backend.service';
import { sha256 } from '../utils/common';
import { generatePreimage, generateTimelock } from '../utils/htlc';
import buildHtlcService from '../utils/buildHtlcService';
import { selectWalletSlice } from '../redux/slices/walletSlice';
import { setModal } from '../redux/slices/modalSlice';

const sleep = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

const useExchangeInit = (): any => {
  const dispatch = useAppDispatch();
  const {
    selectedToken,
    fromChainToken,
    fromChainHtlc,
    selectedFromChain,
    selectedToChain,
    tokenTransferAmount,
  } = useAppSelector(selectBridgeSlice);
  const fromChainWalletAddress = useFromChainWalletAddress();
  const toChainWalletAddress = useToChainWalletAddress();
  const { fromChainWalletProvider } = useAppSelector(selectWalletSlice);

  return React.useCallback(async () => {
    if (
      !tokenTransferAmount ||
      !fromChainToken ||
      !fromChainWalletAddress ||
      !selectedFromChain ||
      !selectedToChain ||
      !selectedToken ||
      !toChainWalletAddress
    ) {
      throw new Error(`Insufficient Parameters`);
    }
    if (fromChainToken.chainSymbol === `KS_BESU`) return; // NOTE: KS_BESU일 경우에는 Confirm 버튼 누르는거 필요하기 때문에 여기에서 처리 X

    const amount = utils
      .parseUnits(tokenTransferAmount!, fromChainToken.decimals)
      .toString();
    const bigAmount = BigNumber.from(amount);
    switch (`${selectedFromChain.symbol} => ${selectedToChain.symbol}`) {
      case `ETH => KS_BESU`: {
        // 1. From체인 지갑에 승인 요청
        const htlcService = await buildHtlcService(
          fromChainWalletProvider,
          selectedFromChain,
          fromChainToken,
          fromChainHtlc,
        );

        const timeoutIdApprove = setTimeout(() => {
          dispatch(setModal(`TimeoutLogout`));
        }, 3600000);

        if (htlcService.approveErc20) {
          await htlcService.approveErc20(
            bigAmount,
            selectedToken.symbol === `USDT`,
          );
        }

        clearTimeout(timeoutIdApprove);

        const timeoutIdNewContract = setTimeout(() => {
          dispatch(setModal(`TimeoutLogout`));
        }, 3600000);

        // 2. 지갑 승인 성공시, 서버에 History 저장
        const preimage = generatePreimage();
        const hashlock = sha256(preimage);
        const timelock = generateTimelock();
        const preimageHex = `0x${Buffer.from(preimage).toString(`hex`)}`;
        const historyData = {
          fromChainSymbol: selectedFromChain.symbol,
          toChainSymbol: selectedToChain.symbol,
          tokenSymbol: fromChainToken.symbol,
          sender: fromChainWalletAddress,
          receiver: toChainWalletAddress,
          fromChainTimelock: timelock.toString(),
          hashlock,
          amount: BigNumber.from(amount).toString(),
        };
        const { id: historyId } = await BridgeBackend.postHistory(historyData);
        dispatch(
          setTxCurrentHistory({
            id: historyId,
            fromEvmChainId: 0,
            toEvmChainId: 0,
            preimg: preimageHex,
            fromChainHtlcState: null,
            toChainHtlcState: null,
            createdAt: dayjs().utc().toISOString(),
            toChainTimelock: null,
            fromTokenAddress: null,
            toTokenAddress: null,
            fromTxHash: null,
            toTxHash: null,
            ...historyData,
          }),
        );
        // preimg 스토리지에 저장
        const oldMap = JSON.parse(
          localStorage.getItem(`preimgHashMap`) || `{}`,
        );
        const preimgHashMap = {
          ...oldMap,
          [hashlock]: preimageHex,
        };
        localStorage.setItem(`preimgHashMap`, JSON.stringify(preimgHashMap));

        // 3. newContract 실행
        try {
          const { tx: newContractTx } = await htlcService.newContract(
            selectedToChain.evmChainId.toString(),
            hashlock,
            timelock,
            toChainWalletAddress,
            bigAmount,
          );
        } catch (e) {
          if (e.code === 4001) {
            await BridgeBackend.reject(historyId);
          }
          dispatch(setTxHasError(true));
        }
        clearTimeout(timeoutIdNewContract);

        return;
      }
      default: {
        console.error(
          `Not implemented yet:`,
          selectedFromChain.symbol,
          `to`,
          selectedToChain.symbol,
        );
      }
    }
  }, [
    fromChainToken,
    tokenTransferAmount,
    fromChainWalletAddress,
    selectedFromChain,
    selectedToChain,
    selectedToken,
    toChainWalletAddress,
    fromChainWalletProvider,
    fromChainHtlc,
    dispatch,
  ]);
};

export default useExchangeInit;
